import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Archive, Trash2, Eye, DownloadCloud } from "lucide-react";
import { toast } from "sonner";
import { employeeCleanupApi } from "../../services/employeeApi";
import { StatusBadge } from "../../components/StatusBadge";
import { EagleState } from "../../components/EagleState";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { usePermissions } from "../../hooks/usePermissions";
import { useLocalization } from "../../hooks/useLocalization";

// Only these two classifications may ever be hard-deleted (mirrors employee_cleanup_service.py).
const DELETE_ELIGIBLE = ["TEST_AUTOMATION", "ORPHANED_TEST_DATA"];
// Never selectable at all - real employees and workforce-migration source records.
const NEVER_SELECTABLE = ["PRODUCTION_CONFIRMED", "MIGRATION_SOURCE"];
const CLASSIFICATIONS = ["TEST_AUTOMATION", "ORPHANED_TEST_DATA", "DEMO_DATA", "DUPLICATE_REVIEW_REQUIRED", "UNCERTAIN_REVIEW_REQUIRED", "MIGRATION_SOURCE", "PRODUCTION_CONFIRMED"];

export default function EmployeeTestDataReviewPage() {
  const { t } = useLocalization();
  const perm = usePermissions();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [selected, setSelected] = useState(new Set());
  const [previewData, setPreviewData] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // "delete" | "archive" | null
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    employeeCleanupApi.listCandidates()
      .then((r) => { setCandidates(r.data); setAccessDenied(false); })
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { ALL: candidates.length };
    CLASSIFICATIONS.forEach((cl) => { c[cl] = candidates.filter((x) => x.classification === cl).length; });
    return c;
  }, [candidates]);

  const visible = filter === "ALL" ? candidates : candidates.filter((c) => c.classification === filter);
  const selectableVisible = visible.filter((c) => !NEVER_SELECTABLE.includes(c.classification));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectVisible = () => setSelected(new Set(selectableVisible.map((c) => c.employee_id)));
  const selectAllEligible = () => setSelected(new Set(candidates.filter((c) => DELETE_ELIGIBLE.includes(c.classification)).map((c) => c.employee_id)));
  const clearSelection = () => setSelected(new Set());

  const runPreview = async () => {
    if (selected.size === 0) { toast.error(t("testDataReview.noneSelected")); return; }
    setBusy(true);
    try {
      const { data } = await employeeCleanupApi.previewBulk(Array.from(selected));
      setPreviewData(data);
    } finally {
      setBusy(false);
    }
  };

  const runBackup = async () => {
    if (selected.size === 0) { toast.error(t("testDataReview.noneSelected")); return; }
    setBusy(true);
    try {
      // Executing with action "delete" always creates a backup first; here we just want the
      // backup step in isolation, so we reuse preview to show the reviewer what it *would*
      // cover, then let them proceed via the delete-confirmation flow for the real backup+delete.
      const { data } = await employeeCleanupApi.previewBulk(Array.from(selected));
      setPreviewData(data);
      toast.info(t("testDataReview.backupCreated", { id: "will be created when you confirm deletion" }));
    } finally {
      setBusy(false);
    }
  };

  const openConfirm = (action) => {
    if (selected.size === 0) { toast.error(t("testDataReview.noneSelected")); return; }
    setConfirmText("");
    setConfirmAction(action);
  };

  const expectedConfirmText = selected.size > 1 ? "DELETE TEST RECORDS" : "DELETE TEST RECORD";

  const executeAction = async () => {
    setBusy(true);
    try {
      const { data } = await employeeCleanupApi.execute(
        Array.from(selected), confirmAction, confirmAction === "delete" ? confirmText : "archive-confirmed"
      );
      toast.success(t("testDataReview.batchComplete", { deleted: data.deleted.length, archived: data.archived.length, blocked: data.blocked.length }));
      setConfirmAction(null);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup action failed");
    } finally {
      setBusy(false);
    }
  };

  if (accessDenied) return <AccessDenied resourceLabel={t("testDataReview.title")} />;
  if (loading) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  return (
    <div className="space-y-5" data-testid="test-data-review-page">
      <Link to="/management/employees" data-testid="back-to-directory-link" className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-600">
        <ArrowLeft size={13} /> {t("nav.employees")}
      </Link>
      <PageHelpBanner pageKey="employee-test-data-review" title="How to use this page">
        <p><strong>Purpose:</strong> Find employee records that were created by test scripts, QA runs, or demos - and safely remove ONLY those, without ever touching real employees.</p>
        <p><strong>Who should use this:</strong> System Owner (full access) and System Administrator (preview only, unless explicitly granted execute rights).</p>
        <p><strong>How to use it:</strong> Filter by classification to review what the system detected. Only TEST_AUTOMATION and ORPHANED_TEST_DATA records are eligible for permanent deletion; real, uncertain, demo, or duplicate records can only be archived. Preview your selection to see exactly what will be removed and why any record is blocked, then type the exact confirmation phrase to permanently delete, or archive instead.</p>
        <p><strong>Common mistake:</strong> Selecting a record you are not sure about - when in doubt, archive it instead of deleting; archiving never destroys data.</p>
        <p><strong>Irreversible:</strong> Permanent deletion cannot be undone from this screen, although a backup of every deleted record is stored server-side before deletion.</p>
      </PageHelpBanner>

      <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm" data-testid="test-data-review-warning-banner">
        <ShieldAlert size={16} className="shrink-0" />
        {t("testDataReview.subtitle")}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="classification-filter-chips">
        <button
          data-testid="filter-chip-ALL"
          onClick={() => setFilter("ALL")}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === "ALL" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
        >
          {t("testDataReview.filterAll")} ({counts.ALL})
        </button>
        {CLASSIFICATIONS.map((cl) => (
          <button
            key={cl}
            data-testid={`filter-chip-${cl}`}
            onClick={() => setFilter(cl)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === cl ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
          >
            {t(`statuses.${cl}`)} ({counts[cl] || 0})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5" data-testid="bulk-action-bar">
        <span className="text-xs font-semibold text-gray-500 mr-2" data-testid="selected-count-label">{t("testDataReview.selectedCount", { count: selected.size })}</span>
        <button data-testid="select-visible-btn" onClick={selectVisible} className="text-xs font-medium px-2.5 py-1 rounded-full border border-gray-300 hover:bg-white">{t("testDataReview.selectVisible")}</button>
        <button data-testid="select-all-eligible-btn" onClick={selectAllEligible} className="text-xs font-medium px-2.5 py-1 rounded-full border border-gray-300 hover:bg-white">{t("testDataReview.selectAllEligible")}</button>
        <button data-testid="clear-selection-btn" onClick={clearSelection} className="text-xs font-medium px-2.5 py-1 rounded-full border border-gray-300 hover:bg-white">{t("testDataReview.clearSelection")}</button>
        <div className="flex-1" />
        {perm.canPreviewTestCleanup && (
          <button data-testid="preview-selected-btn" disabled={busy} onClick={runPreview} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            <Eye size={13} /> {t("testDataReview.previewSelected")}
          </button>
        )}
        {perm.canPreviewTestCleanup && (
          <button data-testid="export-backup-btn" disabled={busy} onClick={runBackup} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-300 hover:bg-white disabled:opacity-50">
            <DownloadCloud size={13} /> {t("testDataReview.exportBackup")}
          </button>
        )}
        {perm.canArchiveEmployee && (
          <button data-testid="archive-selected-btn" disabled={busy} onClick={() => openConfirm("archive")} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
            <Archive size={13} /> {t("testDataReview.archiveSelected")}
          </button>
        )}
        {perm.canExecuteTestCleanup && (
          <button data-testid="delete-eligible-btn" disabled={busy} onClick={() => openConfirm("delete")} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            <Trash2 size={13} /> {t("testDataReview.deleteEligible")}
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 w-10">
                <Checkbox
                  data-testid="test-cleanup-select-all-header-checkbox"
                  checked={selectableVisible.length > 0 && selectableVisible.every((c) => selected.has(c.employee_id))}
                  onCheckedChange={(checked) => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      selectableVisible.forEach((c) => (checked ? next.add(c.employee_id) : next.delete(c.employee_id)));
                      return next;
                    });
                  }}
                />
              </th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Employee ID</th>
              <th className="px-4 py-3 font-medium">Store</th>
              <th className="px-4 py-3 font-medium">Classification</th>
              <th className="px-4 py-3 font-medium">Reasons</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => {
              const disabled = NEVER_SELECTABLE.includes(c.classification);
              return (
                <tr key={c.employee_id} className="border-t border-gray-100" data-testid={`test-cleanup-row-${c.employee_id}`}>
                  <td className="px-4 py-2">
                    <Checkbox
                      data-testid={`test-cleanup-checkbox-${c.employee_id}`}
                      checked={selected.has(c.employee_id)}
                      disabled={disabled}
                      onCheckedChange={() => toggle(c.employee_id)}
                    />
                  </td>
                  <td className="px-4 py-2 font-semibold text-gray-800">{c.legal_name}</td>
                  <td className="px-4 py-2 text-gray-500">{c.employee_code}</td>
                  <td className="px-4 py-2 text-gray-500">{c.store_location || "-"}</td>
                  <td className="px-4 py-2"><StatusBadge status={c.classification} /></td>
                  <td className="px-4 py-2 text-xs text-gray-500 max-w-sm">{c.classification_reasons?.[0]}</td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center"><EagleState variant="empty" title="Nothing here" message="No employees match this filter." testId="test-cleanup-empty" /></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Dry-run preview */}
      <Dialog open={!!previewData} onOpenChange={(open) => !open && setPreviewData(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="cleanup-preview-dialog">
          <DialogHeader><DialogTitle>{t("testDataReview.previewSelected")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(previewData || []).map((p) => (
              <div key={p.employee_id} className="border border-gray-200 rounded-lg p-3" data-testid={`preview-item-${p.employee_id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm">{p.employee_name} ({p.employee_code})</p>
                  <StatusBadge status={p.classification} />
                </div>
                <p className="text-xs text-gray-500 mt-1">{t("testDataReview.dependencyMap")}: {p.dependency_map.total_linked_records} record(s)</p>
                {p.eligible_for_hard_delete ? (
                  <p className="text-xs text-emerald-600 mt-1" data-testid={`preview-eligible-${p.employee_id}`}>{t("testDataReview.eligibleForDelete")} - {t("testDataReview.noProtectedHistory")}</p>
                ) : (
                  <p className="text-xs text-red-600 mt-1" data-testid={`preview-blocked-${p.employee_id}`}>{t("testDataReview.notEligibleForDelete")}: {p.block_reason}</p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm delete / archive */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <DialogContent data-testid="cleanup-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirmAction === "delete" ? t("testDataReview.confirmDeleteTitle") : t("testDataReview.confirmArchiveTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">{t("testDataReview.selectedCount", { count: selected.size })}</p>
          {confirmAction === "delete" && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500">{t("testDataReview.typeToConfirm", { text: expectedConfirmText })}</p>
              <input
                data-testid="cleanup-confirm-text-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={t("testDataReview.confirmationPlaceholder")}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          )}
          <DialogFooter>
            <button data-testid="cleanup-confirm-cancel-btn" onClick={() => setConfirmAction(null)} className="px-4 py-2 rounded-full text-sm font-semibold border border-gray-300">{t("testDataReview.cancel")}</button>
            <button
              data-testid="cleanup-confirm-execute-btn"
              disabled={busy || (confirmAction === "delete" && confirmText !== expectedConfirmText)}
              onClick={executeAction}
              className={`px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-40 ${confirmAction === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`}
            >
              {confirmAction === "delete" ? t("testDataReview.confirmDelete") : t("testDataReview.confirmArchive")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
